"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * While `open`: a press outside the element (mouse, finger or pen) calls `close`. The press is heard as `pointerdown`
 * before anything below the document (capture): a finger's tap brings `mousedown` only as a compatibility event,
 * which never comes when the press was handled (the chart prevents it). A press in an open dialog (a window over the
 * page, e.g. an ⓘ opened from the popup) does not count as outside.
 */
export function useOutsidePress(open: boolean, ref: React.RefObject<HTMLElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const inDialog = (t: EventTarget | null) => t instanceof Element && t.closest('[aria-modal="true"]') !== null;
    const outside = (e: PointerEvent) => ref.current && !ref.current.contains(e.target as Node) && !inDialog(e.target) && close();
    document.addEventListener("pointerdown", outside, true);
    return () => document.removeEventListener("pointerdown", outside, true);
  }, [open, ref, close]);
}

/** A popup's open state and its element: a press outside the element or Escape closes it. */
export function usePopup<T extends HTMLElement>() {
  const [open, setOpen] = useState(false);
  const ref = useRef<T>(null);
  const close = useCallback(() => setOpen(false), []);
  useOutsidePress(open, ref, close);
  useEffect(() => {
    if (!open) return;
    const escape = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [open]);
  return { open, setOpen, ref };
}
